// El panel del Brain: la tercera zona del armazón (2.3, 2.7, D1).
//
// **Empuja, no tapa.** Entra como una zona más del grupo de paneles que ya
// envolvía la aplicación, así que el contenido se estrecha y se sigue viendo.
// La alternativa barata era un cajón superpuesto —`Sheet`, que ya existe y se
// usa en dos sitios—, y se descartó a conciencia: si tapa lo que estás
// mirando, vuelves a tener que abandonar lo que hacías, que es el problema 1
// del planteamiento. Cuando no cabe al lado sí es un cajón, y eso lo decide
// `LocaleLayout` (2.6).
//
// **La conversación no es suya, y por dos motivos.** El primero es que sea la
// misma que la de la pantalla completa (2.7): copiar el envío habría dejado dos
// sitios donde arreglar la confirmación y dos que se irían separando. El
// segundo es que **este componente se desmonta** —al cerrar el panel, y al
// cruzar los 1.280 px, donde deja de ir al lado y pasa a ir encima—. Si la
// conversación viviera aquí, se perdería en las tres cosas. Vive por encima de
// la ruta, en `LocaleLayout` (D7, 2.8), y aquí solo se pinta.
//
// Lo que este panel no tiene, y la pantalla sí: el selector de agente y el
// lateral que se pliega. Son de la pantalla, no de la conversación. **La lista
// de conversaciones sí la tiene desde la 4.4**, en el título: volver a una no
// debería obligar a dejar la página en la que se está.

import { useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Maximize2, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ChatInput } from '@/components/brain/ChatInput'
import { Mensaje } from '@/components/brain/Mensaje'
import { BrainHistoryMenu } from '@/components/brain/BrainHistoryMenu'
import { ReadingFile } from '@/components/brain/ReadingFile'
import { useGuidedUpload } from '@/contexts/guided-upload'
import { useBrainModelsQuery } from '@/queries/brain'
import type { useBrainConversation } from '@/hooks/use-brain-conversation'

export function BrainPanel({
  conversacion,
  onCerrar,
}: {
  conversacion: ReturnType<typeof useBrainConversation>
  onCerrar: () => void
}) {
  const { t } = useTranslation()
  const { lang } = useParams<{ lang: string }>()
  const finRef = useRef<HTMLDivElement>(null)
  const askForFile = useGuidedUpload()
  const {
    messages,
    inputValue,
    setInputValue,
    isLoading,
    nombresHerramientas,
    handleSendMessage,
    detener,
    confirmar,
    cancelar,
    responder,
    elegirArchivo,
    dictar,
    dictando,
    transcribiendo,
    microfono,
    resolveProposal,
    resolvingProposal,
    readingFile,
    nuevoHilo,
    hiloActivo,
    abrirHilo,
    modelo,
    elegirModelo,
  } = conversacion
  const modelos = useBrainModelsQuery().data ?? []

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [messages])

  return (
    <aside
      aria-label={t('brain.title')}
      // Sin borde propio: al lado lo separa el tirador del armazon, y encima
      // se lo pone el cajon (`sheet.tsx:43`). Ponerlo aqui pintaba una segunda
      // linea pegada a la primera, y el presupuesto de cajas lo cazo.
      className="flex h-full min-h-0 flex-col bg-background"
    >
      <div className="flex h-[52px] shrink-0 items-center justify-between px-4">
        <BrainHistoryMenu activeThread={hiloActivo} onOpen={abrirHilo} onNew={nuevoHilo} />
        <div className="flex items-center gap-1">
          {/* **Empezar de nuevo sin cerrar el panel** (5.4, D4, R6). Cerrar no
              borra la conversacion a proposito —se pide algo en una pantalla y
              se sigue hablando en otra—, asi que lo que faltaba no era borrarla
              al cerrar: era poder reiniciar cuando quieras. Y no se pierde
              nada: la anterior se abre desde la lista del Brain.

              Solo con algo que reiniciar: un boton que no hace nada visible es
              peor que no tenerlo. */}
          {messages.length > 0 && (
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label={t('brain.new_chat')}
              onClick={nuevoHilo}
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
          {/* La unica puerta a la pantalla completa desde que el Brain salio
              del menu (2.2). La ruta sigue existiendo; lo que se quito es la
              segunda entrada, no la pantalla. */}
          <Button asChild size="icon-xs" variant="ghost">
            <Link to={`/${lang ?? 'es'}/brain`} aria-label={t('brain.panel.completo')}>
              <Maximize2 className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={t('brain.panel.cerrar')}
            onClick={onCerrar}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <Separator />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('brain.welcome_desc')}</p>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <Mensaje
                key={message.id}
                message={message}
                nombresHerramientas={nombresHerramientas}
                onConfirmar={confirmar}
                onCancelar={cancelar}
                onAnswer={responder}
                onFileChoice={elegirArchivo}
                ocupado={isLoading}
                onResolveProposal={resolveProposal}
                resolvingProposal={resolvingProposal}
              />
            ))}
            <ReadingFile name={readingFile} />
            <div ref={finRef} />
          </div>
        )}
      </div>

      <Separator />
      <div className="shrink-0 space-y-4 px-4 py-3">
        {/* Sin selector de agente: aquí se habla con el Brain, que es el que
            opera la plataforma. Elegir a quién hablarle es de la pantalla
            completa, donde hay sitio para esa decisión. */}
        <ChatInput
          inputValue={inputValue}
          setInputValue={setInputValue}
          isLoading={isLoading}
          onSend={handleSendMessage}
          onStop={detener}
          placeholder={t('brain.input_placeholder')}
          addMessagePlaceholder={t('brain.input_placeholder')}
          // El clip abre el selector del armazón, el mismo de «Subir
          // documento»: lo que se elija entra en esta conversación (6.2).
          onAttach={askForFile}
          models={modelos}
          chosenModel={modelo}
          onModelSelect={elegirModelo}
          onToggleRecording={dictar}
          isRecording={dictando}
          isTranscribing={transcribiendo}
          recordingStream={microfono}
        />
      </div>
    </aside>
  )
}
